import usePushEnrollment from '../hooks/usePushEnrollment';
import { pushSupported } from '../utils/pushFirebase';

function Toggle({ on, disabled, accentOn = '#3E2C6E' }) {
  return (
    <div style={{
      width: 48, height: 26, borderRadius: 13, flexShrink: 0,
      background: on ? accentOn : '#ccc', opacity: disabled ? 0.5 : 1,
      position: 'relative', pointerEvents: disabled ? 'none' : 'auto',
    }}>
      <div style={{
        position: 'absolute', top: 3, right: on ? 3 : 25,
        width: 20, height: 20, borderRadius: '50%', background: '#fff',
        transition: 'right 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
      }} />
    </div>
  );
}

export default function PushSetup({ lang = 'ar', compact = false, theme = 'light' }) {
  const { status, loading, error, subscribed, available, iosHint, setEnabled } = usePushEnrollment(lang);
  const isRider = theme === 'rider';

  if (!available && status && !status.global_enabled) {
    return compact || isRider ? null : (
      <div style={styles.wrap}>
        <div style={styles.title}>{lang === 'ar' ? 'إشعارات النظام' : 'System notifications'}</div>
        <div style={styles.hint}>{lang === 'ar' ? 'معطلة من مدير النظام' : 'Disabled by admin'}</div>
      </div>
    );
  }

  if (status && status.global_enabled && !status.plan_allows) {
    return compact || isRider ? null : (
      <div style={styles.wrap}>
        <div style={styles.title}>{lang === 'ar' ? 'إشعارات النظام' : 'Push'}</div>
        <div style={styles.hint}>{lang === 'ar' ? 'غير متاحة في باقتك' : 'Not on your plan'}</div>
      </div>
    );
  }

  const title = lang === 'ar' ? 'إشعارات النظام' : 'System notifications';
  const hintOff = lang === 'ar' ? 'تنبيهات على الجهاز عند إغلاق التطبيق' : 'Alerts when app is closed';
  const hintOn = lang === 'ar' ? 'مفعّلة' : 'On';

  if (isRider) {
    return (
      <RiderRow
        label={title}
        sub={error || (subscribed ? hintOn : hintOff)}
        subColor={error ? '#F87171' : undefined}
        on={() => !loading && setEnabled(!subscribed)}
      >
        <Toggle on={subscribed} disabled={loading} accentOn="#10B981" />
      </RiderRow>
    );
  }

  return (
    <div style={{ ...styles.wrap, ...(compact ? { padding: '8px 0', background: 'transparent', border: 'none' } : {}) }}>
      <div style={styles.row}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={styles.title}>{title}</div>
          <div style={styles.hint}>{subscribed ? hintOn : hintOff}</div>
          {iosHint && !subscribed && <div style={styles.warn}>{iosHint}</div>}
          {error && <div style={styles.err}>{error}</div>}
        </div>
        <button
          type="button"
          disabled={loading}
          onClick={() => setEnabled(!subscribed)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: loading ? 'wait' : 'pointer' }}
        >
          <Toggle on={subscribed} disabled={loading} />
        </button>
      </div>
    </div>
  );
}

/** Rider V2 dark-theme row — used inside settings cards */
export function RiderPushRow({ lang = 'ar', onToggle, last = false, borderColor = '#1C2A40' }) {
  const { loading, error, subscribed, status, setEnabled } = usePushEnrollment(lang);

  if (status === null) return null;
  const show = pushSupported() && status.global_enabled && status.plan_allows && status.enabled;
  if (!show) return null;

  const title = lang === 'ar' ? 'إشعارات النظام' : 'System push';
  const sub = error
    || (subscribed
      ? (lang === 'ar' ? 'مفعّلة على هذا الجهاز' : 'Enabled on this device')
      : (lang === 'ar' ? 'تنبيهات عند إغلاق التطبيق' : 'Alerts when app is closed'));

  return (
    <RiderRow
      label={title}
      sub={sub}
      subColor={error ? '#F87171' : undefined}
      last={last}
      borderColor={borderColor}
      on={() => {
        if (loading) return;
        setEnabled(!subscribed);
        onToggle?.(!subscribed);
      }}
    >
      <Toggle on={subscribed} disabled={loading} accentOn="#10B981" />
    </RiderRow>
  );
}

function RiderRow({ label, sub, subColor, children, last, on, borderColor }) {
  return (
    <div
      onClick={on}
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '14px 16px', gap: 12, cursor: on ? 'pointer' : 'default',
        borderBottom: last ? 'none' : `1px solid ${borderColor || '#1C2A40'}`,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: '#F0F6FF', fontSize: 13, fontWeight: 600 }}>{label}</div>
        {sub && (
          <div style={{ color: subColor || '#8BA3C0', fontSize: 11, marginTop: 3, lineHeight: 1.4 }}>{sub}</div>
        )}
      </div>
      {children}
    </div>
  );
}

const styles = {
  wrap: { padding: '14px 16px', borderTop: '1px solid #eee', background: '#fafafa' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14 },
  title: { fontSize: 14, fontWeight: 700, color: '#333' },
  hint: { fontSize: 12, color: '#666', marginTop: 4 },
  warn: { fontSize: 11, color: '#e65100', marginTop: 8, background: '#fff3e0', padding: '8px 10px', borderRadius: 8 },
  err: { fontSize: 11, color: '#c62828', marginTop: 8, background: '#ffebee', padding: '8px 10px', borderRadius: 8 },
};
